import React, { useEffect, useMemo } from "react";
import SeccionCard from "../SeccionCard";
import useAutosave from "../useAutosave";
import { guardarSignos } from "../guardado";

const CAMPOS = [
  { k: "temperatura", label: "Temperatura °C", pattern: "[0-9]+([.][0-9]+)?" },
  { k: "pulso", label: "Pulso / min", pattern: "[0-9]+" },
  { k: "presion", label: "Presión arterial", pattern: "[0-9]+[/][0-9]+" },
  { k: "frecuencia_respiratoria", label: "Fr. respiratoria", pattern: "[0-9]+([.][0-9]+)?" },
  { k: "saturacion_oxigeno", label: "Sat. oxígeno %", pattern: "[0-9]+([.][0-9]+)?" },
  { k: "peso", label: "Peso / kg", pattern: "[0-9]+([.][0-9]+)?" },
  { k: "talla", label: "Talla / m", pattern: "[0-9]+([.][0-9]+)?" },
];

/**
 * IMC: si el backend ya trae un valor calculado se muestra ese; si no, se
 * calcula en presentación (peso / talla²) — misma fórmula que el componente
 * actual. Campo de solo lectura marcado como calculado.
 */
function calcularImc(peso, talla, imcBackend) {
  const p = parseFloat(peso);
  const t = parseFloat(talla);
  if (!isNaN(p) && !isNaN(t) && t > 0) {
    return { valor: (p / (t * t)).toFixed(2), fuente: "calc" };
  }
  if (imcBackend !== undefined && imcBackend !== null && String(imcBackend) !== "") {
    return { valor: String(imcBackend), fuente: "backend" };
  }
  return { valor: "", fuente: null };
}

export default function SeccionSignos({
  seccionId,
  signos,
  onChangeSignos,
  ctx,
  bloqueado,
  puedeGuardar,
  onNavegar,
  registrarEstado,
  amberFoot,
  amberMsg,
}) {
  const imc = useMemo(() => calcularImc(signos.peso, signos.talla, signos.imc), [signos.peso, signos.talla, signos.imc]);

  // El valor de IMC que se envía sigue el mismo criterio que hoy: lo calcula el
  // frontend. Lo mantenemos sincronizado en el objeto que se guarda.
  const valorParaGuardar = useMemo(() => ({ ...signos, imc: imc.valor || signos.imc || "" }), [signos, imc.valor]);

  const autosave = useAutosave({
    value: valorParaGuardar,
    delay: 1000,
    enabled: !bloqueado && puedeGuardar,
    serialize: (v) => JSON.stringify(CAMPOS.map((c) => v[c.k] ?? "").concat(v.imc ?? "")),
    save: (v) => guardarSignos(ctx, v),
    onSaved: (data) => {
      if (Array.isArray(data) && data[0]) onChangeSignos({ ...signos, ...data[0], __refrescoServidor: true });
    },
  });

  useEffect(() => {
    registrarEstado(seccionId, autosave);
  }, [seccionId, autosave, registrarEstado]);

  const set = (k, v) => onChangeSignos({ ...signos, [k]: v });

  return (
    <SeccionCard
      seccionId={seccionId}
      autosave={autosave}
      onNavegar={onNavegar}
      amberFoot={amberFoot}
      amberMsg={amberMsg}
    >
      {signos.__origenRecepcion && (
        <div className="cev__franja">
          <span aria-hidden="true">📋</span>
          Ya registrados{signos.fecha ? ` el ${signos.fecha}` : ""}{signos.hora ? ` a las ${signos.hora}` : ""} (habitualmente en recepción). Corrige lo que haga falta.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
        {CAMPOS.map((c) => (
          <div key={c.k}>
            <label className="form-label" htmlFor={`cev-sv-${c.k}`}>{c.label}</label>
            <input
              id={`cev-sv-${c.k}`}
              type="text"
              className="form-control"
              autoComplete="off"
              pattern={c.pattern}
              disabled={bloqueado || !puedeGuardar}
              value={signos[c.k] ?? ""}
              onChange={(e) => set(c.k, e.target.value)}
              onBlur={() => autosave.flush()}
            />
          </div>
        ))}
        <div>
          <label className="form-label" htmlFor="cev-sv-imc">IMC</label>
          <input id="cev-sv-imc" type="text" className="form-control" value={imc.valor} readOnly aria-readonly="true" />
          <div className="cev__calc-hint">
            {imc.fuente === "backend" ? "Calculado por el sistema" : "Calculado (peso / talla²)"}
          </div>
        </div>
      </div>

      {!puedeGuardar && (
        <div className="cev__calc-hint" style={{ marginTop: 8 }}>
          No tienes permiso para registrar signos vitales.
        </div>
      )}
    </SeccionCard>
  );
}
